import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  Room,
  RoomOptions,
  RemoteTrack,
  RoomEvent,
  Track,
  LocalAudioTrack,
  RemoteAudioTrack,
  Participant,
  TranscriptionSegment,
  ConnectionState,
} from 'livekit-client';
import ky from '@/utils/ky';

export interface VoiceConfig {
  language: string;
  prompt: string;
  firstMessage: string;
  voiceId?: string;
  roomName: string;
  participantName: string;
  // Optional LiveKit language config preset (forwarded as `config` to /livekit/token).
  livekitConfigPreset?: string;
}

export interface VoiceCallbacks {
  onConnect?: () => void;
  onError?: (error: any) => void;
  onMessage?: (data: { source: 'ai' | 'user'; message: string }) => void;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
}

interface UseLiveKitParams {
  roomName?: string;
  participantName?: string;
  metadata?: any;
  onTranscriptUpdate?: (transcript: TranscriptionSegment[]) => void;
  onConnectionChange?: (isConnected: boolean) => void;
  onAgentSpeaking?: (isSpeaking: boolean) => void;
}

interface UseLiveKitReturn {
  // Basic LiveKit functionality
  room: Room | null;
  isConnected: boolean;
  isConnecting: boolean;
  transcript: TranscriptionSegment[];
  isAgentSpeaking: boolean;
  audioLevel: number;
  connectionState: ConnectionState;
  connectConversation: (
    roomName?: string,
    participantName?: string,
    metadata?: any,
  ) => Promise<void>;
  disconnectConversation: () => void;
  startMicrophone: () => Promise<void>;
  stopMicrophone: () => void;
  sendMessage: (message: any) => void;
  clearTranscript: () => void;
  isMicrophoneActive: boolean;
  error: string | null;
  // Voice Engine Interface
  startVoiceSession: (
    config: VoiceConfig,
    callbacks?: VoiceCallbacks,
  ) => Promise<string>;
  stopVoiceSession: () => Promise<void>;
}

export const useLiveKit = (params: UseLiveKitParams = {}): UseLiveKitReturn => {
  console.log('🎯 DEBUG: useLiveKit hook initialized');
  console.log(
    '🎯 DEBUG: Track.Source.Microphone enum value:',
    Track.Source.Microphone,
  );
  console.log('🎯 DEBUG: All Track.Source values:', Object.keys(Track.Source));

  // Add stack trace to see what's causing re-initialization
  // console.trace('🎯 DEBUG: useLiveKit hook initialization stack trace');
  const {
    roomName: defaultRoomName,
    participantName: defaultParticipantName,
    metadata: defaultMetadata,
    onTranscriptUpdate,
    onConnectionChange,
    onAgentSpeaking,
  } = params;

  // State
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptionSegment[]>([]);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  );
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);

  // Debug: Track microphone state changes
  useEffect(() => {
    console.log('🎤 DEBUG: Microphone state changed:', {
      isMicrophoneActive,
      isConnected,
      roomState: room?.state,
      mediaStreamActive: mediaStreamRef.current?.active,
      hasAudioTrack: !!audioTrackRef.current,
      timestamp: new Date().toISOString(),
    });

    // If microphone was turned off unexpectedly, log stack trace
    if (!isMicrophoneActive && mediaStreamRef.current?.active) {
      console.warn(
        '🚨 WARNING: Microphone set to inactive but media stream is still active!',
      );
      console.trace('Stack trace for microphone deactivation:');
    }
  }, [isMicrophoneActive, isConnected, room?.state]);

  // CRITICAL: Prevent microphone from being disabled due to re-renders
  useEffect(() => {
    if (
      isMicrophoneActive &&
      room &&
      room.localParticipant &&
      mediaStreamRef.current?.active
    ) {
      console.log(
        '🎤 DEBUG: Ensuring microphone stays enabled during re-render',
      );
      room.localParticipant.setMicrophoneEnabled(true);
    }
  }, [isMicrophoneActive, room]);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);
  const audioTrackRef = useRef<LocalAudioTrack | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const monitorIntervalsRef = useRef<NodeJS.Timeout[]>([]);
  const callbacksRef = useRef<VoiceCallbacks>({});
  const isMicrophoneActiveRef = useRef(isMicrophoneActive);
  const isAgentSpeakingRef = useRef(isAgentSpeaking);
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const shouldStartMicrophoneRef = useRef(false);
  const startMicrophoneRef = useRef<(() => Promise<void>) | null>(null);

  // Update refs when state changes
  useEffect(() => {
    isMicrophoneActiveRef.current = isMicrophoneActive;
  }, [isMicrophoneActive]);

  useEffect(() => {
    isAgentSpeakingRef.current = isAgentSpeaking;
  }, [isAgentSpeaking]);

  const roomOptions = useMemo(
    (): RoomOptions => ({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: {
        simulcast: true,
      },
    }),
    [],
  );

  // Get access token from backend
  const getAccessToken = useCallback(
    async (roomName: string, participantName: string, configPreset?: string) => {
      try {
        const searchParams: Record<string, string> = {
          username: participantName,
          room: roomName,
          livekitAgentName: import.meta.env.VITE_LIVEKIT_AGENT_NAME,
        };
        if (configPreset) {
          searchParams.config = configPreset;
        }
        const response = await ky
          .get('livekit/token', { searchParams })
          .json<{ token: string }>();

        return response.token;
      } catch (error) {
        console.error('Failed to get LiveKit access token:', error);
        throw new Error('Failed to authenticate with LiveKit server');
      }
    },
    [],
  );

  // Audio track management
  const attachAudioTrack = useCallback(
    (track: RemoteAudioTrack, participantIdentity: string) => {
      const existingElement = audioElementsRef.current.get(participantIdentity);
      if (existingElement) {
        existingElement.remove();
        audioElementsRef.current.delete(participantIdentity);
      }

      const audioElement = track.attach();
      audioElement.id = `audio-${participantIdentity}`;
      audioElement.style.display = 'none';
      audioElement.volume = 1.0;

      // Auto-play with retry logic
      const playAudio = async () => {
        try {
          await audioElement.play();
        } catch (error) {
          console.log('Auto-play failed, user interaction required:', error);
          // Add click listener to play audio on user interaction
          const playOnClick = () => {
            audioElement.play().catch(console.error);
            document.removeEventListener('click', playOnClick);
          };
          document.addEventListener('click', playOnClick);
        }
      };

      document.body.appendChild(audioElement);
      audioElementsRef.current.set(participantIdentity, audioElement);
      playAudio();
    },
    [],
  );

  // Room event listeners setup
  const setupRoomListeners = useCallback(
    (room: Room) => {
      const handleTrackSubscribed = (
        track: RemoteTrack,
        _publication: any,
        participant: Participant,
      ) => {
        console.log('📺 DEBUG: Track subscribed:', {
          trackKind: track.kind,
          participantIdentity: participant.identity,
          trackSid: track.sid,
          publicationSource: _publication?.source,
          publicationName: _publication?.name,
          timestamp: new Date().toISOString(),
        });

        // Log source information specifically for microphone tracks
        if (_publication?.source) {
          console.log(
            '📺 DEBUG: SOURCE MICROPHONE - Track subscription source:',
            _publication.source,
          );
        }

        if (track.kind === Track.Kind.Audio) {
          attachAudioTrack(track as RemoteAudioTrack, participant.identity);
        }
      };

      const handleTrackUnsubscribed = (
        track: RemoteTrack,
        _publication: any,
        participant: Participant,
      ) => {
        console.log('📺 DEBUG: Track unsubscribed:', {
          trackKind: track.kind,
          participantIdentity: participant.identity,
          trackSid: track.sid,
          timestamp: new Date().toISOString(),
        });
        if (track.kind === Track.Kind.Audio) {
          const audioElement = audioElementsRef.current.get(
            participant.identity,
          );
          if (audioElement) {
            audioElement.remove();
            audioElementsRef.current.delete(participant.identity);
          }
        }
      };

      const handleParticipantAttributesChanged = (
        changedAttributes: Record<string, string>,
        participant: Participant,
      ) => {
        console.log('🔄 DEBUG: Participant attributes changed:', {
          participantIdentity: participant.identity,
          changedAttributes,
          timestamp: new Date().toISOString(),
        });
        if (participant.identity.startsWith('agent-')) {
          const agentState = changedAttributes['lk.agent.state'];
          const wasSpeaking = isAgentSpeakingRef.current;
          const nowSpeaking = agentState === 'speaking';

          console.log('🤖 DEBUG: Agent speaking state change:', {
            participantIdentity: participant.identity,
            agentState,
            wasSpeaking,
            nowSpeaking,
            microphoneActive: isMicrophoneActiveRef.current,
            timestamp: new Date().toISOString(),
          });

          setIsAgentSpeaking(nowSpeaking);

          // Check if agent just stopped speaking and microphone is still active
          if (wasSpeaking && !nowSpeaking && isMicrophoneActiveRef.current) {
            console.log(
              '🎤 DEBUG: Agent finished speaking, microphone should remain active',
            );
          }

          // CRITICAL: Prevent microphone from being turned off when agent changes state
          if (agentState === 'listening' && isMicrophoneActiveRef.current) {
            console.log(
              '🎤 DEBUG: Agent is now listening - ensuring microphone stays active',
            );
            // Explicitly maintain microphone state
            if (room && room.localParticipant) {
              room.localParticipant.setMicrophoneEnabled(true);
              console.log(
                '🎤 DEBUG: Re-enabled microphone to ensure it stays active',
              );
            }
          }
        }
      };

      const handleTranscriptionReceived = (
        segments: TranscriptionSegment[],
        participant?: Participant,
      ) => {
        console.log('📝 DEBUG: Transcription received:', {
          segmentCount: segments.length,
          participantIdentity: participant?.identity,
          segments: segments.map((s) => ({
            id: s.id,
            text: s.text,
            final: s.final,
          })),
        });

        setTranscript((prev) => {
          const newTranscript = [...prev];

          segments.forEach((segment) => {
            if (segment.text.length > 3 || segment.final) {
              const existingIndex = newTranscript.findIndex(
                (t) => t.id === segment.id,
              );

              if (existingIndex >= 0) {
                newTranscript[existingIndex] = segment;
              } else {
                newTranscript.push(segment);
              }

              // Trigger onMessage callback for voice interface
              if (segment.final) {
                // Create unique key for deduplication
                const messageKey = `${segment.id}-${segment.text}`;

                // Skip if already processed
                if (processedMessagesRef.current.has(messageKey)) {
                  console.log('📝 DEBUG: Skipping duplicate message:', {
                    messageKey,
                    text: segment.text,
                  });
                  return;
                }

                // Mark as processed
                processedMessagesRef.current.add(messageKey);

                // Determine source based on participant identity
                let source: 'ai' | 'user' = 'user';

                // Use participant parameter to determine source
                if (participant?.identity) {
                  source = participant.identity.startsWith('agent-')
                    ? 'ai'
                    : 'user';
                }
                // Additional fallback: check if this looks like an agent message
                else if (
                  segment.text.toLowerCase().includes('coaching assistant') ||
                  segment.text.toLowerCase().includes('how can i help')
                ) {
                  source = 'ai';
                }

                console.log('📝 DEBUG: Message source determined:', {
                  source,
                  text: segment.text,
                  messageKey,
                  callbackParticipant: participant?.identity,
                });

                callbacksRef.current.onMessage?.({
                  source,
                  message: segment.text,
                });
              }

              // Trigger onTranscript callback
              callbacksRef.current.onTranscript?.(segment.text, segment.final);
            }
          });

          return newTranscript.sort((a, b) => a.startTime - b.startTime);
        });
      };

      const handleConnectionStateChanged = (state: ConnectionState) => {
        setConnectionState(state);
        setIsConnected(state === ConnectionState.Connected);
      };

      const handleLocalTrackPublished = (
        publication: any,
        participant: Participant,
      ) => {
        console.log('🎤 DEBUG: Local track published:', {
          trackSid: publication.trackSid,
          kind: publication.kind,
          source: publication.source,
          name: publication.name,
          participantIdentity: participant.identity,
          timestamp: new Date().toISOString(),
        });

        // Specifically log microphone source information
        if (publication.source) {
          console.log(
            '🎤 DEBUG: SOURCE MICROPHONE - Local track published with source:',
            publication.source,
          );
        }
      };

      const handleDisconnected = () => {
        console.log('🔌 DEBUG: Room disconnected event fired');
        setIsConnected(false);
        setIsConnecting(false);
        // Check if microphone was active and log
        if (isMicrophoneActiveRef.current) {
          console.log(
            '🎤 DEBUG: Microphone was active when room disconnected - may cause issues',
          );
          console.log(
            '🎤 DEBUG: setIsMicrophoneActive(false) called from room disconnect event',
          );
          setIsMicrophoneActive(false);
        }
      };

      const handleSignalConnected = async () => {
        console.log('🎤 DEBUG: Signal connected - safe to publish tracks');
        // Only auto-start microphone if it was requested
        if (shouldStartMicrophoneRef.current && startMicrophoneRef.current) {
          try {
            await startMicrophoneRef.current();
            shouldStartMicrophoneRef.current = false;
          } catch (error) {
            console.error(
              'Failed to start microphone on signal connected:',
              error,
            );
          }
        }
      };

      // Set up event listeners
      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.on(
        RoomEvent.ParticipantAttributesChanged,
        handleParticipantAttributesChanged,
      );
      room.on(RoomEvent.TranscriptionReceived, handleTranscriptionReceived);
      room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
      room.on(RoomEvent.Disconnected, handleDisconnected);
      room.on(RoomEvent.SignalConnected, handleSignalConnected);

      // Store cleanup functions
      const cleanup = () => {
        room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
        room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
        room.off(
          RoomEvent.ParticipantAttributesChanged,
          handleParticipantAttributesChanged,
        );
        room.off(RoomEvent.TranscriptionReceived, handleTranscriptionReceived);
        room.off(
          RoomEvent.ConnectionStateChanged,
          handleConnectionStateChanged,
        );
        room.off(RoomEvent.Disconnected, handleDisconnected);
        room.off(RoomEvent.SignalConnected, handleSignalConnected);
      };

      cleanupFunctionsRef.current.push(cleanup);
    },
    [attachAudioTrack],
  );

  // Core room connection
  const connectToRoom = useCallback(
    async (roomName: string, participantName: string, _metadata?: any) => {
      if (isConnecting || isConnected) {
        console.log('Already connecting or connected to room');
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        const token = await getAccessToken(roomName, participantName);
        const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

        if (!livekitUrl) {
          throw new Error('VITE_LIVEKIT_URL environment variable is not set');
        }

        const newRoom = new Room(roomOptions);
        setupRoomListeners(newRoom);

        await newRoom.connect(livekitUrl, token);

        setRoom(newRoom);
        setIsConnecting(false);

        console.log('Successfully connected to LiveKit room:', roomName);
        console.log(
          '🔌 DEBUG: Room connection complete, isConnected will be set to:',
          true,
        );
      } catch (error) {
        console.error('Failed to connect to LiveKit room:', error);
        setIsConnecting(false);
        setError(error instanceof Error ? error.message : 'Connection failed');
        throw error;
      }
    },
    [
      isConnecting,
      isConnected,
      setupRoomListeners,
      roomOptions,
      getAccessToken,
    ],
  );

  const connectConversation = useCallback(
    async (roomName?: string, participantName?: string, metadata?: any) => {
      console.log('🔌🔌🔌 DEBUG: connectConversation CALLED!!!', {
        roomName,
        participantName,
        metadata,
      });
      try {
        setError(null);

        const finalRoomName = roomName || defaultRoomName;
        const finalParticipantName = participantName || defaultParticipantName;
        const finalMetadata = metadata || defaultMetadata;

        if (!finalRoomName || !finalParticipantName) {
          throw new Error('Room name and participant name are required');
        }

        await connectToRoom(finalRoomName, finalParticipantName, finalMetadata);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to connect to conversation';
        setError(errorMessage);
        throw err;
      }
    },
    [defaultRoomName, defaultParticipantName, defaultMetadata, connectToRoom],
  );

  const disconnectFromRoom = useCallback(() => {
    if (room) {
      // Clean up audio elements
      audioElementsRef.current.forEach((element) => {
        element.remove();
      });
      audioElementsRef.current.clear();

      // Clean up event listeners
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
      cleanupFunctionsRef.current = [];

      // Disconnect from room
      room.disconnect();
      setRoom(null);
      setIsConnected(false);
      setIsConnecting(false);
      setTranscript([]);
      setIsAgentSpeaking(false);
      setAudioLevel(0);
      setConnectionState(ConnectionState.Disconnected);

      console.log('Disconnected from LiveKit room');
    }
  }, [room]);

  const disconnectConversation = useCallback(() => {
    try {
      setError(null);

      // Stop microphone inline to avoid circular dependency
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      console.log(
        '🎤 DEBUG: setIsMicrophoneActive(false) called from disconnectConversation',
      );
      setIsMicrophoneActive(false);

      // Clear processed messages to prevent memory leaks
      processedMessagesRef.current.clear();

      disconnectFromRoom();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to disconnect from conversation';
      setError(errorMessage);
      console.error('Error disconnecting:', err);
    }
  }, [disconnectFromRoom]);

  const startMicrophone = useCallback(async () => {
    if (
      isMicrophoneActive ||
      !isConnected ||
      !room ||
      room.state !== ConnectionState.Connected
    ) {
      console.warn('Cannot start microphone: room not fully connected');
      return;
    }

    try {
      setError(null);

      // Stop existing tracks properly
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      // Get user media stream
      console.log('🎤 DEBUG: Requesting getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
        video: false,
      });
      console.log('🎤 DEBUG: getUserMedia successful:', {
        streamId: stream.id,
        audioTracks: stream.getAudioTracks().length,
        trackInfo: stream.getAudioTracks().map((t) => ({
          id: t.id,
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState,
        })),
      });

      // Store the stream reference to prevent it from being garbage collected
      mediaStreamRef.current = stream;

      // Use the stream directly for LiveKit track
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) {
        throw new Error('No audio track found in stream');
      }

      console.log('🎤 DEBUG: Audio track details:', {
        id: audioTrack.id,
        label: audioTrack.label,
        enabled: audioTrack.enabled,
        readyState: audioTrack.readyState,
        muted: audioTrack.muted,
      });

      // Add event listeners to track state changes
      audioTrack.addEventListener('ended', () => {
        console.log('🎤 DEBUG: Audio track ended unexpectedly!', {
          trackId: audioTrack.id,
          timestamp: new Date().toISOString(),
        });
        console.trace('Stack trace for track ended event:');
        console.log(
          '🎤 DEBUG: setIsMicrophoneActive(false) called from track ended event',
        );
        setIsMicrophoneActive(false);
      });

      audioTrack.addEventListener('mute', () => {
        console.log('🎤 DEBUG: Audio track muted!', {
          trackId: audioTrack.id,
          timestamp: new Date().toISOString(),
        });
      });

      audioTrack.addEventListener('unmute', () => {
        console.log('🎤 DEBUG: Audio track unmuted!', {
          trackId: audioTrack.id,
          timestamp: new Date().toISOString(),
        });
      });

      // Publish the raw media stream track
      console.log('🎤 DEBUG: Publishing track to LiveKit...');
      console.log(
        '🎤 DEBUG: Track.Source.Microphone value:',
        Track.Source.Microphone,
      );

      try {
        const publication = await room!.localParticipant.publishTrack(
          audioTrack,
          {
            source: Track.Source.Microphone,
            name: 'user-microphone',
          },
        );
        console.log('🎤 DEBUG: Track published successfully:', {
          publicationSid: publication.trackSid,
          kind: publication.kind,
          source: publication.source,
          sourceValue: Track.Source.Microphone,
        });
        console.log(
          '🎤 DEBUG: SOURCE MICROPHONE - Track source is:',
          publication.source,
        );
      } catch (error) {
        if (
          error instanceof Error &&
          error.message?.includes('not connected')
        ) {
          console.warn('Track publishing failed - room not ready, retrying...');
          // Wait a bit and retry
          setTimeout(() => startMicrophone(), 1000);
          return;
        }
        throw error;
      }

      // Enable microphone
      room!.localParticipant.setMicrophoneEnabled(true);
      console.log('🎤 DEBUG: Microphone enabled on participant');

      console.log(
        '🎤 DEBUG: setIsMicrophoneActive(true) called from startMicrophone',
      );
      setIsMicrophoneActive(true);
      console.log('🎤 Microphone started and track published successfully');

      // Debug: Monitor track state periodically
      const monitorInterval = setInterval(() => {
        if (mediaStreamRef.current && audioTrack) {
          console.log('🎤 DEBUG: Periodic track check:', {
            streamActive: mediaStreamRef.current.active,
            trackEnabled: audioTrack.enabled,
            trackReadyState: audioTrack.readyState,
            trackMuted: audioTrack.muted,
            roomConnected: room?.state === 'connected',
            microphoneActive: isMicrophoneActive,
            timestamp: new Date().toISOString(),
          });
        } else {
          console.log('🎤 DEBUG: Track monitor stopped - refs cleared');
          clearInterval(monitorInterval);
        }
      }, 5000); // Check every 5 seconds

      // Store interval reference for cleanup
      monitorIntervalsRef.current.push(monitorInterval);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to start microphone';
      setError(errorMessage);
      console.error('❌ Error starting microphone:', err);
      callbacksRef.current.onError?.(err);
      throw err;
    }
  }, [isConnected, room, isMicrophoneActive]);

  // Update the ref whenever startMicrophone changes
  useEffect(() => {
    startMicrophoneRef.current = startMicrophone;
  }, [startMicrophone]);

  const stopMicrophone = useCallback(() => {
    console.log('🔇 DEBUG: stopMicrophone called');
    console.log('🔇 Stopping microphone...');

    // Clear monitor intervals
    monitorIntervalsRef.current.forEach(clearInterval);
    monitorIntervalsRef.current = [];

    // Stop the media stream tracks
    if (mediaStreamRef.current) {
      console.log('🔇 DEBUG: Stopping media stream tracks:', {
        streamId: mediaStreamRef.current.id,
        trackCount: mediaStreamRef.current.getTracks().length,
      });
      mediaStreamRef.current.getTracks().forEach((track) => {
        console.log(
          `🔇 DEBUG: Stopping track: ${track.label} (ID: ${track.id}, state: ${track.readyState})`,
        );
        track.stop();
      });
      mediaStreamRef.current = null;
    } else {
      console.log('🔇 DEBUG: No media stream to stop');
    }

    // Stop local audio track
    if (audioTrackRef.current) {
      audioTrackRef.current.stop();
      audioTrackRef.current = null;
    }

    // Unpublish all audio tracks from LiveKit
    if (room && room.localParticipant) {
      console.log('🔇 DEBUG: Unpublishing LiveKit tracks:', {
        publicationCount: room.localParticipant.audioTrackPublications.size,
      });
      room.localParticipant.audioTrackPublications.forEach((publication) => {
        if (publication.track) {
          console.log(`🔇 DEBUG: Unpublishing track: ${publication.trackSid}`);
          room.localParticipant.unpublishTrack(publication.track);
        }
      });
      // Disable microphone
      room.localParticipant.setMicrophoneEnabled(false);
      console.log('🔇 DEBUG: Microphone disabled on participant');
    } else {
      console.log('🔇 DEBUG: No room or participant to unpublish from');
    }

    console.log(
      '🎤 DEBUG: setIsMicrophoneActive(false) called from stopMicrophone',
    );
    setIsMicrophoneActive(false);
    setError(null);
    console.log('✅ Microphone stopped successfully');
  }, [room]);

  const sendMessage = useCallback(
    (message: any) => {
      if (!room || !isConnected) {
        console.warn('Cannot send message: not connected to room');
        return;
      }

      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(message));
        room.localParticipant.publishData(data, { reliable: true });
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },
    [room, isConnected],
  );

  const clearTranscript = useCallback(() => {
    setTranscript([]);
  }, []);

  // Voice Engine Interface Methods
  const startVoiceSession = useCallback(
    async (
      config: VoiceConfig,
      callbacks: VoiceCallbacks = {},
    ): Promise<string> => {
      console.log('🚀🚀🚀 DEBUG: startVoiceSession ABSOLUTELY CALLED!!!');
      console.log('🚀 DEBUG: startVoiceSession ENTRY - function called!');
      try {
        console.log('🚀 DEBUG: startVoiceSession called:', {
          roomName: config.roomName,
          participantName: config.participantName,
          currentlyConnected: isConnected,
          currentlyConnecting: isConnecting,
          currentMicActive: isMicrophoneActive,
        });

        // Prevent starting if already connected or connecting (prevents double-click issues)
        if (isConnected || isConnecting) {
          console.warn(
            '🚨 Voice session already active or connecting - ignoring duplicate start request',
          );
          return `${config.roomName}-${config.participantName}`;
        }

        callbacksRef.current = callbacks;

        const metadata = {
          language: config.language,
          prompt: config.prompt,
          firstMessage: config.firstMessage,
          voiceId: config.voiceId,
        };
        console.log('🚀 DEBUG: Voice session config:', metadata);

        // Set connecting state to prevent duplicate starts
        setIsConnecting(true);

        // Get token and connect to room (following working example pattern)
        console.log('🔌 DEBUG: Connecting to room...');
        const token = await getAccessToken(
          config.roomName,
          config.participantName,
          config.livekitConfigPreset,
        );
        const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

        if (!livekitUrl) {
          setIsConnecting(false);
          throw new Error('VITE_LIVEKIT_URL environment variable is not set');
        }

        const newRoom = new Room(roomOptions);
        setupRoomListeners(newRoom);
        await newRoom.connect(livekitUrl, token);

        // Wait for room to be fully connected before publishing tracks
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Room connection timeout'));
          }, 10000); // 10 second timeout

          if (newRoom.state === ConnectionState.Connected) {
            clearTimeout(timeout);
            resolve();
          } else {
            const onStateChanged = (state: ConnectionState) => {
              if (state === ConnectionState.Connected) {
                clearTimeout(timeout);
                newRoom.off(RoomEvent.ConnectionStateChanged, onStateChanged);
                resolve();
              } else if (state === ConnectionState.Disconnected) {
                clearTimeout(timeout);
                newRoom.off(RoomEvent.ConnectionStateChanged, onStateChanged);
                reject(new Error('Room disconnected during connection'));
              }
            };
            newRoom.on(RoomEvent.ConnectionStateChanged, onStateChanged);
          }
        });

        console.log('Successfully connected to LiveKit room:', config.roomName);
        setRoom(newRoom);
        setIsConnected(true);
        setIsConnecting(false);

        // Now start microphone after confirmed connection
        console.log(
          '🎤 DEBUG: Publishing audio tracks after confirmed connection...',
        );
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 48000,
            },
          });

          console.log('🎤 DEBUG: getUserMedia successful:', {
            streamId: stream.id,
            audioTracks: stream.getAudioTracks().length,
            trackInfo: stream.getAudioTracks().map((t) => ({
              id: t.id,
              label: t.label,
              enabled: t.enabled,
              readyState: t.readyState,
            })),
          });

          // Store the stream reference
          mediaStreamRef.current = stream;

          // Verify room is still connected before publishing
          if (newRoom.state !== ConnectionState.Connected) {
            throw new Error('Room disconnected before track publishing');
          }

          // Publish each audio track (following working example pattern)
          for (const track of stream.getAudioTracks()) {
            console.log(
              `🎤 DEBUG: Publishing track: ${track.label} (ID: ${track.id})`,
            );

            // Double-check connection state before each track
            if (newRoom.state !== ConnectionState.Connected) {
              throw new Error('Room disconnected during track publishing');
            }

            const publication = await newRoom.localParticipant.publishTrack(
              track,
              {
                source: Track.Source.Microphone,
                name: 'user-microphone',
              },
            );

            console.log('🎤 DEBUG: Track published successfully:', {
              publicationSid: publication.trackSid,
              kind: publication.kind,
              source: publication.source,
            });
            console.log(
              '🎤 DEBUG: SOURCE MICROPHONE - Track source is:',
              publication.source,
            );
          }

          // Enable microphone
          newRoom.localParticipant.setMicrophoneEnabled(true);
          console.log('🎤 DEBUG: Microphone enabled on participant');

          setIsMicrophoneActive(true);
          console.log('🎤 Microphone started and track published successfully');
        } catch (audioError) {
          console.error('❌ Error publishing audio tracks:', audioError);

          // Clean up resources on error
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
          }

          // Disconnect the room if connection failed
          if (newRoom) {
            newRoom.disconnect();
          }
          setRoom(null);
          setIsConnected(false);
          setIsConnecting(false);

          // Handle specific microphone permission errors (like in RolePlay.tsx)
          if (audioError instanceof Error) {
            // Check for connection state errors first
            if (
              audioError.message?.includes('not connected') ||
              audioError.message?.includes('disconnected') ||
              audioError.message?.includes('UnexpectedConnectionState')
            ) {
              const connectionError = new Error('LIVEKIT_CONNECTION_ERROR');
              callbacks.onError?.(connectionError);
              throw connectionError;
            }

            const isIframe = window.self !== window.top;
            const isPermissionError =
              audioError.name === 'NotAllowedError' ||
              audioError.message?.includes('Permissions policy');

            if (isIframe && isPermissionError) {
              const iframeError = new Error('MICROPHONE_IFRAME_PERMISSION');
              callbacks.onError?.(iframeError);
              throw iframeError;
            } else if (audioError.name === 'NotAllowedError') {
              const deniedError = new Error('MICROPHONE_DENIED');
              callbacks.onError?.(deniedError);
              throw deniedError;
            }
          }

          callbacks.onError?.(audioError);
          throw new Error(
            'Failed to access microphone. Please check your permissions.',
          );
        }

        console.log('✅ DEBUG: Voice session started successfully');
        callbacks.onConnect?.();
        return `${config.roomName}-${config.participantName}`;
      } catch (err) {
        console.error('❌ DEBUG: startVoiceSession error:', err);
        console.error('❌ DEBUG: Error details:', {
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          errorStack: err instanceof Error ? err.stack : 'No stack trace',
          config,
          currentState: {
            isConnected,
            isConnecting,
            isMicrophoneActive,
            roomState: room?.state,
          },
        });
        // Reset connecting state on error
        setIsConnecting(false);
        callbacks.onError?.(err);
        throw err;
      }
    },
    [
      getAccessToken,
      setupRoomListeners,
      roomOptions,
      isConnected,
      isConnecting,
      isMicrophoneActive,
      room?.state,
    ],
  );

  const stopVoiceSession = useCallback(async () => {
    stopMicrophone();
    disconnectFromRoom();
  }, [stopMicrophone, disconnectFromRoom]);

  // Handle transcript updates
  useEffect(() => {
    if (onTranscriptUpdate) {
      onTranscriptUpdate(transcript);
    }
  }, [transcript, onTranscriptUpdate]);

  // Handle connection changes
  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(isConnected);
    }
  }, [isConnected, onConnectionChange]);

  // Handle agent speaking changes
  useEffect(() => {
    if (onAgentSpeaking) {
      onAgentSpeaking(isAgentSpeaking);
    }
  }, [isAgentSpeaking, onAgentSpeaking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear monitor intervals
      monitorIntervalsRef.current.forEach(clearInterval);
      monitorIntervalsRef.current = [];

      // Clean up media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      disconnectFromRoom();
    };
  }, [disconnectFromRoom]);

  const hookResult = {
    // Basic LiveKit functionality
    room,
    isConnected,
    isConnecting,
    transcript,
    isAgentSpeaking,
    audioLevel,
    connectionState,
    connectConversation,
    disconnectConversation,
    startMicrophone,
    stopMicrophone,
    sendMessage,
    clearTranscript,
    isMicrophoneActive,
    error,
    // Voice Engine Interface
    startVoiceSession,
    stopVoiceSession,
  };

  console.log('🎯 DEBUG: useLiveKit hook returning methods:', {
    hasStartVoiceSession: typeof hookResult.startVoiceSession === 'function',
    hasStartMicrophone: typeof hookResult.startMicrophone === 'function',
    hasConnectConversation:
      typeof hookResult.connectConversation === 'function',
  });

  return hookResult;
};
